import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { calcDirection, LinieValidationManager } from 'src/app/shared/classes/linie-validation-manager';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { Table } from 'src/app/models/ui/table';
import { CachedMediaStore } from 'src/app/shared/stores/media-cached.store';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { AnlagenMediaApiFuncService, MEDIA_TITLE } from 'src/app/services/MediaApiFunc/anlagen-media-api-func.service';
import { FileObject } from 'src/app/services/base64/base64.service';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { BaseItemsWrapperComponent } from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { rxResource } from '@angular/core/rxjs-interop';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';
import { ifNotError } from 'src/app/misc/compositions';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-bauwerksdialog',
  templateUrl: './bauwerksdialog.component.html',
  styleUrls: ['./bauwerksdialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    BaseItemsWrapperComponent,
    PreviewThumbnailsComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    LinieDrehungPipe,
    ArrayMethodsCallbackPipe,
  ],
  providers: [
    LinieDrehungPipe,
    { provide: MEDIA_TITLE, useValue: 'Bauwerke' },
    { provide: MediaApiFunc, useClass: AnlagenMediaApiFuncService },
    CachedMediaStore,
    GeoAutofill,
  ],
})
export class BauwerksDialogComponent extends LinieValidationManager {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly cachedMedia = inject(CachedMediaStore);
  private readonly confirmService = inject(ConfirmService);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  protected readonly drehung = inject(LinieDrehungService);

  public readonly closedialog = new BehaviorSubject(false);
  private dataRefresh: EventEmitter<any>;

  private readonly preview = viewChild(PreviewThumbnailsComponent);
  private readonly grid = viewChild(ClrDatagrid);
  private readonly clrForm = viewChild(ClrForm);

  readonly otypid = model<number>(undefined);
  readonly ostammid = model<number>(undefined);
  readonly stammData = model<{ startmeter: number; endmeter: number }>(null);
  private readonly startmeter = computed(() => this.stammData().startmeter);
  private readonly endmeter = computed(() => this.stammData().endmeter);
  protected readonly direction = computed(() => calcDirection(this.startmeter(), this.endmeter()));
  protected readonly selectedBauw = signal<BauwerkRow>(null);

  protected exceptStartEnd = (col: any) => !['von', 'bis'].includes(col.id);
  protected readonly table = signal<Table<BauwerkRow>>(undefined);
  private readonly bauartenResource = rxResource({
    params: this.otypid,
    stream: ({ params }) => this.apiService.getBauwerksArten(params),
  });
  protected readonly artListe = ifNotError(this.bauartenResource);
  protected readonly isInspektion = signal(true);
  protected readonly isBaseModalOpen = signal(false);
  protected readonly deletedFiles = signal<string[]>([]);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  public set data(dataIn: any) {
    this.isInspektion.set(false);
    if (dataIn.table) this.table.set(dataIn.table);
    this.ostammid.set(dataIn.ostamm);
    this.otypid.set(dataIn.otypid);
    this.stammData.set(dataIn.stammData);
    this.dataRefresh = dataIn.dataRefresh;
  }

  protected formBauTop = new FormGroup({
    von: new FormControl<number>(null),
    bis: new FormControl<number>(null),
    bauwerksart: new FormControl<string>(null, [Validators.required]),
    bezeichnung: new FormControl<string>(null),
    lage: new FormControl<string>(null),
    gpskoordinaten: new FormControl<string>(null),
  });

  constructor() {
    super({ start: 'von', end: 'bis' }, true);
    effect(() => {
      if (!(this.startmeter() - this.endmeter())) return;
      this.initLinieValidators(this.startmeter(), this.endmeter(), !this.isInspektion(), this.formBauTop);
    });
    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    this.geoAutofill.registerOSTAMMID(this.ostammid);

    effect(() => {
      if (this.ostammid() && (this.isInspektion() || !this.table().columns.length)) untracked(() => this.freshReload());
    });

    effect(() => {
      const selected = this.selectedBauw();
      untracked(() => {
        this.formBauTop.reset(selected ?? undefined);
        if (!selected) return;
        this.clrForm().markAsTouched();
        this.cachedMedia.getBilder(selected.id, selected.bild, this.preview());
      });
    });
  }

  protected addBauw() {
    this.grid()?.selection.clearSelection();
    this.formBauTop.reset();

    setTimeout(() => {
      this.preview().clearPreview();
    });
  }

  protected async deleteTitelBauw() {
    const text = this.mrTranslate.transform('Bauwerk wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (ok && this.selectedBauw()) {
      const id = this.selectedBauw().id;
      const { success, error } = await firstValueFrom(this.apiService.deleteBauw(this.ostammid(), id, this.otypid()));
      if (success) {
        this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
        this.freshReload();
        this.cachedMedia.deleteInCache(id, this.preview());
        this.grid()?.selection.clearSelection();
        this.dataRefresh?.emit(true);
      } else {
        console.log(error);
        this.toastr.error(this.mrTranslate.transform('Fehler beim Löschen'));
      }
    }
  }

  private async freshReload() {
    const bawerkeTable = await firstValueFrom(this.apiService.getBauw(this.ostammid()));
    this.table.update(table => {
      if (!table?.columns?.length) table = bawerkeTable;
      else table.rows = bawerkeTable.rows;
      return { ...table };
    });
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.formBauTop.valid) this.saveBauw();
  }

  private async saveBauw() {
    this.saveState.set(ClrLoadingState.LOADING);

    const sendObj: BauwerkeModel = {
      ID: this.selectedBauw()?.id ?? -1,
      table: this.formBauTop.value,
      mediaChange: {
        files: await this.preview().packUnsavedFiles(),
        savedFiles: this.preview().savedNames() ?? '',
        deletedFiles: this.deletedFiles(),
      },
    };

    const { success, error, id } = await firstValueFrom(this.apiService.setBauw(this.ostammid(), this.otypid(), sendObj));

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.cachedMedia.reloadAfterSaved(id, this.preview(), this.deletedFiles());
      this.deletedFiles.set([]);
      this.freshReload();
      this.grid().selected = [{ id }];
      this.dataRefresh?.emit(true);
      this.saveState.set(ClrLoadingState.SUCCESS);
    } else {
      console.log(error);
      this.toastr.error(this.mrTranslate.transform('Fehler beim Speichern'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  public checkLinieValid(startmeter: number, endmeter: number) {
    this.stammData.set({ startmeter, endmeter });

    const valid = this.isLinieValid(this.table().rows, startmeter, endmeter);
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Start- & Endmeter'),
        this.mrTranslate.transform('Bauwerke')
      );
    return valid;
  }

  protected async findPositionAndAutofill(mode: 'von' | 'bis') {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter(),
      end: this.endmeter(),
      direction: this.direction(),
    });

    this.formBauTop.controls[mode].setValue(position);
  }

  protected deleteFile(name: string) {
    this.deletedFiles.update(names => (names.push(name), [...names]));
  }

  protected close() {
    this.closedialog.next(true);
  }
}

export interface BauwerkRow {
  id: number;
  ostammid: number;
  bparentid: number;
  von: number;
  bis: number;
  bauwerksart: string;
  bezeichnung: string;
  lage: string;
  zustand: string;
  bild: string;
  gpskoordinaten: string;
  benutzername: string;
  status: string;
}

export interface BauwerkeModel {
  ID: number;
  table: Partial<{
    von: number;
    bis: number;
    bauwerksart: string;
    bezeichnung: string;
    lage: string;
    gpskoordinaten: string;
  }>;
  mediaChange: {
    files: FileObject[];
    savedFiles: string;
    deletedFiles: string[];
  };
}
